/**
 * MuteBanner — shows a friendly red banner at the top of every page when the
 * signed-in user has an active moderation mute (auto or manual).
 *
 * Renders nothing if the user is not muted or the mute has expired. Reads
 * `muted_until` + `muted_reason` from the AuthContext user object (populated
 * by /api/auth/me). Re-checks every minute so the banner disappears when the
 * mute expires without requiring a hard refresh.
 */
import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useAuth } from "../context/AuthContext";

function formatWhen(iso) {
  try {
    const d = new Date(iso);
    // e.g. "Aug 5, 4:41 PM"
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return "soon";
  }
}

function hoursFrom(iso) {
  try {
    const ms = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 3600000));
  } catch {
    return 0;
  }
}

export default function MuteBanner() {
  const { user } = useAuth();
  const [, tick] = useState(0);

  // Re-render every 60s so the banner auto-dismisses when the mute expires.
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  if (!user?.muted_until) return null;
  const untilMs = new Date(user.muted_until).getTime();
  if (isNaN(untilMs) || untilMs <= Date.now()) return null;

  const hoursLeft = hoursFrom(user.muted_until);
  const when = formatWhen(user.muted_until);
  const reason = user.muted_reason || "multiple content removals";

  return (
    <div
      className="w-full bg-red-50 border-b-2 border-red-500 px-4 py-3"
      role="alert"
      data-testid="mute-banner"
    >
      <div className="max-w-6xl mx-auto flex items-start sm:items-center gap-3 flex-col sm:flex-row">
        <div className="w-8 h-8 shrink-0 flex items-center justify-center bg-red-500 text-white">
          <AlertTriangle className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono-num text-[10px] uppercase tracking-widest text-red-700 font-black">
            // You're muted until {when}
          </div>
          <div className="text-sm text-slate-900 leading-snug mt-0.5">
            You can browse but can't post, reply, or send messages for the next{" "}
            <b>{hoursLeft} hour{hoursLeft === 1 ? "" : "s"}</b>. Reason: <span className="italic text-slate-700">{reason}.</span>{" "}
            <a href="/terms#community-standards" className="text-red-700 underline hover:text-red-900 font-semibold">
              Read the community standards
            </a>{" "}
            or contact <a href="mailto:support@theaminoexchange.com" className="text-red-700 underline hover:text-red-900 font-semibold">support</a> if you think this is a mistake.
          </div>
        </div>
      </div>
    </div>
  );
}
